import { useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';

import _ from 'lodash';

import { baseApiUrl, fetcher, ServerErrorResponse, createParamString } from 'data_sources/constants';
import datetimeHelper from 'helpers/datetimeHelper';
import { generateRiskPoolMetrics } from './riskPoolMetricsGenerator';
import { useRiskPoolDemoData } from './useRiskPoolDemoData';

import type {
  RiskPoolOrganization,
  RiskPoolOrganizationsApiResponse,
  RiskPoolOrganizationsResponse,
} from '../../_types/risk_pool';

interface RiskPoolOrganizationsParams {
  date?: string;
  search?: string;
  compliance?: string;
  sortOrder?: string;
  limit?: number;
  offset?: number;
  [key: string]: any;
}

/** Default date for risk-pool organizations API: yesterday in YYYY-MM-DD format. */
const getDefaultDate = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};

// Apply filters to organizations demo data
const applyFilters = (data: RiskPoolOrganization[], params: RiskPoolOrganizationsParams): RiskPoolOrganization[] => {
  let filteredData = [...data];

  if (!_.isEmpty(params.search)) {
    filteredData = _.filter(filteredData, (org) => org.name.toLowerCase().includes(params.search!.toLowerCase()));
  }

  if (!_.isEmpty(params.compliance)) {
    filteredData = _.filter(filteredData, (org) => org.compliancePerformance === params.compliance);
  }

  return filteredData;
};

export const getRiskPoolOrganizations = async (
  orgId: string,
  params: RiskPoolOrganizationsParams,
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<RiskPoolOrganizationsResponse> => {
  const url = `${baseApiUrl()}/activity-center/v1/organizations/${orgId}/risk-pool/policy/organizations`;

  const apiParams = {
    ...params,
    limit: params.limit ?? 25,
    date: params.date ?? getDefaultDate(),
  };

  const paramString = createParamString(apiParams);

  try {
    const apiData: RiskPoolOrganizationsApiResponse = await fetcher({
      method: 'get',
      url: `${url}?${paramString}`,
      options: { signal },
    });
    return {
      organizations: apiData.content,
      totalCount: apiData.count,
      offset: apiData.offset,
    };
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    }
    throw error;
  }
};

export default function useRiskPoolOrganizations(
  orgId: string,
  params: RiskPoolOrganizationsParams = {},
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const isDemoDataEnabled = useRiskPoolDemoData();
  const isDateValid = params.date ? datetimeHelper.isValidDateString(params.date) : true;

  const paramsWithDefaults = useMemo(() => ({
    ...params,
    date: params.date ?? getDefaultDate(),
  }), [params]);

  return useInfiniteQuery<RiskPoolOrganizationsResponse, Error>(
    ['risk_pool_organizations', orgId, paramsWithDefaults],
    async ({ signal, pageParam }): Promise<RiskPoolOrganizationsResponse> => {
      if (isDemoDataEnabled) {
        let data = generateRiskPoolMetrics(orgId, paramsWithDefaults);
        data = applyFilters(data, paramsWithDefaults);
        return { organizations: data, totalCount: data.length, offset: 0 };
      }

      const offset = pageParam?.offset ?? params.offset ?? 0;
      return await getRiskPoolOrganizations(orgId, { ...paramsWithDefaults, offset }, signal, onError);
    },
    {
      ...options,
      enabled: !!orgId && isDateValid,
      keepPreviousData: true,
      refetchOnWindowFocus: true,
      refetchInterval: isDemoDataEnabled ? false : 1000 * 60 * 2,
      getNextPageParam: (lastPage) => {
        if (lastPage.offset && lastPage.offset < lastPage.totalCount && lastPage.organizations.length > 0) {
          return { offset: lastPage.offset };
        }
        return undefined;
      },
    }
  );
}
